/**
 * Shared dark command-card Mapbox popup chrome.
 *
 * Mapbox paints `.mapboxgl-popup-content` white from its own stylesheet and
 * often wins the cascade against app CSS. These helpers use INLINE styles so
 * the look cannot regress, and `paintDarkMapPopupChrome` rewrites Mapbox
 * chrome after mount.
 */
import React, { useEffect } from 'react';

export const DARK_MAP_POPUP_BG = '#1a1f27';
export const DARK_MAP_POPUP_CLASS = 'dark-map-popup';

export function paintDarkMapPopupChrome(className: string = DARK_MAP_POPUP_CLASS) {
  const root = document.querySelector(`.mapboxgl-popup.${className}`) as HTMLElement | null;
  if (!root) return;
  const content = root.querySelector('.mapboxgl-popup-content') as HTMLElement | null;
  if (content) {
    content.style.setProperty('background', DARK_MAP_POPUP_BG, 'important');
    content.style.setProperty('background-color', DARK_MAP_POPUP_BG, 'important');
    content.style.setProperty('color', 'rgba(255,255,255,0.92)', 'important');
    content.style.setProperty('padding', '0', 'important');
    content.style.setProperty('border-radius', '8px', 'important');
    content.style.setProperty(
      'box-shadow',
      '0 16px 40px rgba(0,0,0,0.65), 0 0 0 1px rgba(255,255,255,0.07)',
      'important',
    );
    content.style.setProperty('overflow', 'hidden', 'important');
  }
  const tip = root.querySelector('.mapboxgl-popup-tip') as HTMLElement | null;
  if (tip) {
    tip.style.setProperty('border-top-color', DARK_MAP_POPUP_BG, 'important');
    tip.style.setProperty('border-bottom-color', DARK_MAP_POPUP_BG, 'important');
  }
  const close = root.querySelector('.mapboxgl-popup-close-button') as HTMLElement | null;
  if (close) {
    close.style.setProperty('color', 'rgba(255,255,255,0.45)', 'important');
    close.style.setProperty('font-size', '16px', 'important');
    close.style.setProperty('z-index', '3', 'important');
  }
}

/** Re-paint Mapbox chrome shortly after a popup opens. */
export function useDarkMapPopupChrome(active: boolean, className: string = DARK_MAP_POPUP_CLASS) {
  useEffect(() => {
    if (!active) return;
    paintDarkMapPopupChrome(className);
    const t0 = window.setTimeout(() => paintDarkMapPopupChrome(className), 0);
    const t1 = window.setTimeout(() => paintDarkMapPopupChrome(className), 50);
    return () => {
      window.clearTimeout(t0);
      window.clearTimeout(t1);
    };
  }, [active, className]);
}

export const DarkMapPopupRow: React.FC<{ label: string; mono?: boolean; children: React.ReactNode }> = ({
  label,
  mono,
  children,
}) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 5 }}>
    <span
      style={{
        flex: '0 0 58px',
        color: 'rgba(255,255,255,0.38)',
        fontWeight: 600,
        fontSize: '0.62rem',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        paddingTop: 2,
      }}
    >
      {label}
    </span>
    <span
      style={{
        flex: 1,
        minWidth: 0,
        color: 'rgba(255,255,255,0.92)',
        wordBreak: 'break-word',
        fontSize: mono ? '0.72rem' : '0.76rem',
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' : 'inherit',
      }}
    >
      {children}
    </span>
  </div>
);

export const DarkMapPopupCoords: React.FC<{ lat: number; lon: number }> = ({ lat, lon }) => (
  <div
    style={{
      marginTop: 8,
      paddingTop: 8,
      borderTop: '1px solid rgba(255,255,255,0.08)',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      fontSize: '0.7rem',
      color: 'rgba(255,255,255,0.55)',
    }}
  >
    {Number(lat).toFixed(5)}, {Number(lon).toFixed(5)}
  </div>
);

export const DarkMapPopupShell: React.FC<{
  accent: string;
  title: string;
  badge?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  onClose?: () => void;
}> = ({ accent, title, badge, children, actions, onClose }) => (
  <div
    style={{
      position: 'relative',
      background: DARK_MAP_POPUP_BG,
      color: 'rgba(255,255,255,0.92)',
      borderLeft: `3px solid ${accent}`,
      padding: '12px 14px 10px',
      minWidth: 220,
      maxWidth: 300,
      fontSize: '0.75rem',
      lineHeight: 1.45,
      boxSizing: 'border-box',
    }}
  >
    {onClose && (
      <button
        type="button"
        aria-label="Close"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        style={{
          position: 'absolute',
          top: 4,
          right: 6,
          border: 'none',
          background: 'transparent',
          color: 'rgba(255,255,255,0.45)',
          fontSize: 16,
          lineHeight: 1,
          cursor: 'pointer',
          padding: '4px 6px',
        }}
      >
        ×
      </button>
    )}
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, margin: '0 18px 10px 0' }}>
      <strong
        style={{
          display: 'block',
          fontWeight: 650,
          fontSize: '0.84rem',
          margin: 0,
          color: accent,
          wordBreak: 'break-word',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        }}
      >
        {title}
      </strong>
      {badge ? (
        <span
          style={{
            flexShrink: 0,
            marginTop: 1,
            padding: '1px 6px',
            border: '1px solid rgba(255,255,255,0.28)',
            borderRadius: 3,
            fontSize: '0.58rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.75)',
          }}
        >
          {badge}
        </span>
      ) : null}
    </div>
    {children}
    {actions ? (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          marginTop: 10,
          paddingTop: 8,
          borderTop: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        {actions}
      </div>
    ) : null}
  </div>
);

export const darkMapPopupActionBtnStyle = (accent?: boolean): React.CSSProperties => ({
  border: 'none',
  background: 'transparent',
  color: accent ? '#69f0ae' : 'rgba(255,255,255,0.55)',
  fontSize: '0.65rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  padding: 0,
});

/** @deprecated Prefer DarkMapPopup* names; kept for Pathfinder call-site compatibility. */
export const PfPopupRow = DarkMapPopupRow;
export const PfPopupCoords = DarkMapPopupCoords;
export const PfPopupShell = DarkMapPopupShell;
export const pfActionBtnStyle = darkMapPopupActionBtnStyle;
export const paintPathfinderPopupChrome = () => paintDarkMapPopupChrome(DARK_MAP_POPUP_CLASS);
export const PF_POPUP_BG = DARK_MAP_POPUP_BG;
